import type { Timestamp } from "firebase/firestore";
import type { Contact, ContactSource, ContactStatus } from "@/types/contacts";
import { str, strOrNull, arr } from "@/lib/normalizeFields";

/**
 * Fills in every field `Contact` requires with a safe default, defending
 * against the fan-in create path (functions/src/contacts/fanIn/upsertFromEndUser.ts,
 * pre-fix) that stamped a partial doc — no `tagIds`/`workshopIds`, and no
 * name fields for a nameless endUser. All 748 production contacts were
 * created that way. Every caller of a raw Firestore doc for `contacts/*`
 * MUST route through this before treating the result as a `Contact` —
 * readers (ContactsPage's Tags cell, ContactDetailSheet) index into these
 * fields unguarded and crash with "Cannot read properties of undefined" on
 * a doc that skipped this. Client reads get it automatically through
 * `converters.contacts` (src/lib/firestore.ts), whose fromFirestore
 * delegates here.
 */
export function normalizeContact(data: Record<string, unknown>): Contact {
  return {
    normalizedEmail: str(data.normalizedEmail),
    displayEmail: str(data.displayEmail),
    firstName: str(data.firstName),
    lastName: str(data.lastName),
    phone: strOrNull(data.phone),
    company: strOrNull(data.company),
    roleTitle: strOrNull(data.roleTitle),

    tagIds: arr(data.tagIds),
    orgIds: arr(data.orgIds),
    studioIds: arr(data.studioIds),
    workshopIds: arr(data.workshopIds),
    linkedEndUserIds: arr(data.linkedEndUserIds),

    primaryEndUserId: str(data.primaryEndUserId),
    isHouseOnly: typeof data.isHouseOnly === "boolean" ? data.isHouseOnly : false,

    source: (typeof data.source === "string" ? data.source : "manual") as ContactSource,
    importBatchId: strOrNull(data.importBatchId),

    // "active" only when the field is genuinely absent/malformed — an
    // admin's archive decision (a real, present "archived") must never be
    // overridden by a defaulting pass.
    status: (typeof data.status === "string" ? data.status : "active") as ContactStatus,
    archivedAt: (data.archivedAt ?? null) as Timestamp | null,
    archivedBy: strOrNull(data.archivedBy),

    lastActivityAt: (data.lastActivityAt ?? null) as Timestamp | null,

    // Timestamps are passed through as-is when present; a genuinely missing
    // createdAt/updatedAt (never expected from either write path) falls
    // back to null rather than fabricating a Timestamp value. Every reader
    // that touches these (formatTimestamp/formatTimestampRelative) already
    // treats a non-Timestamp value as "—", so this degrades safely.
    createdAt: (data.createdAt ?? null) as Timestamp,
    createdBy: str(data.createdBy),
    updatedAt: (data.updatedAt ?? null) as Timestamp,
  };
}
